import {
  COUNTERPARTY_IDENTITY_TABLE_NAME,
  COUNTERPARTY_ROLE_TABLE_NAME,
  CounterpartyIdentity,
  CounterpartyIdentityLookup,
  CounterpartyIdentityStatus,
  CounterpartyRole,
  CounterpartyRoleTargetType,
  cleanInputUpdate,
  createCounterpartyIdentityLookup,
  deleteCounterpartyIdentity as deleteCounterpartyIdentityMutation,
  findCounterpartyIdentity,
  getCounterpartyIdentity as getCounterpartyIdentityQuery,
  getCounterpartyRole as getCounterpartyRoleQuery,
  getLearnedRoleId,
  getPointIdentityIdsOfTransaction,
  getTableClientId,
  Transaction,
  TransactionLink,
  updateCounterpartyIdentity as updateCounterpartyIdentityMutation,
} from '@rentguru/commons-utils';
import { deleteAndHideEntityWithFetchBefore, get, list, mutation } from '@up2rent/graphql';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import { ReactNode, Reducer, createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import {
  filterIdentitiesVisibleOnLeases,
  getRoleIdsOfRemovedTargets,
  getRolesDeletedWithIdentity,
} from 'src/components/Accounting/Transactions/CounterpartyIdentities/counterpartyIdentitiesUtils';

import { useLeases } from './LeasesContext';
import { useUser } from './UserContext';
import {
  CounterpartyIdentitiesAction,
  CounterpartyIdentitiesState,
  counterpartyIdentitiesReducer,
  hasActiveEvidenceOfTransaction,
  INITIAL_COUNTERPARTY_IDENTITIES_STATE,
} from './utils/counterpartyIdentitiesStateUtils';
import { usePermissions } from './utils/PermissionsContext';

export interface CounterpartyIdentitiesContextType {
  counterpartyIdentities: CounterpartyIdentity[];
  counterpartyRoles: CounterpartyRole[];
  counterpartyIdentityLookup: CounterpartyIdentityLookup;
  counterpartyIdentitiesFetched: boolean;
  counterpartyIdentitiesLoading: boolean;
  counterpartyIdentitiesError?: string;
  refetchCounterpartyIdentities: () => void;
  suspendIdentity: (identity: CounterpartyIdentity) => Promise<CounterpartyIdentity>;
  reactivateIdentity: (identity: CounterpartyIdentity) => Promise<CounterpartyIdentity>;
  deleteIdentity: (identity: CounterpartyIdentity) => Promise<CounterpartyIdentity | null>;
  isLearnedFromTransaction: (transaction: TransactionBeforeDecision, target: LearnedTarget) => Promise<boolean>;
  isExclusionLearnedFromTransaction: (transaction: LearnedTransaction) => Promise<boolean>;
}

// The evidence lists only serve the reads of a single record, and would multiply the size of the register
const LIST_COUNTERPARTY_IDENTITIES_QUERY = /* GraphQL */ `
  query SyncCounterpartyIdentities(
    $filter: ModelCounterpartyIdentityFilterInput
    $limit: Int
    $nextToken: String
    $lastSync: AWSTimestamp
  ) {
    syncCounterpartyIdentities(filter: $filter, limit: $limit, nextToken: $nextToken, lastSync: $lastSync) {
      items {
        id
        keyType
        key
        isFullRemittance
        kind
        contactId
        source
        status
        evidenceCount
        firstSeenAt
        lastSeenAt
        lastCounterpartName
        lastDecisionId
        exclusionReason
        exclusionComment
        readId
        clientId
        createdAt
        updatedAt
        _version
        _deleted
        _lastChangedAt
        __typename
      }
      nextToken
      startedAt
      __typename
    }
  }
`;

const LIST_COUNTERPARTY_ROLES_QUERY = /* GraphQL */ `
  query SyncCounterpartyRoles(
    $filter: ModelCounterpartyRoleFilterInput
    $limit: Int
    $nextToken: String
    $lastSync: AWSTimestamp
  ) {
    syncCounterpartyRoles(filter: $filter, limit: $limit, nextToken: $nextToken, lastSync: $lastSync) {
      items {
        id
        subjectId
        targetType
        targetId
        source
        status
        evidenceCount
        lastSeenAt
        lastDecisionId
        readId
        clientId
        createdAt
        updatedAt
        _version
        _deleted
        _lastChangedAt
        __typename
      }
      nextToken
      startedAt
      __typename
    }
  }
`;

type LearnedTransaction = Pick<Transaction, 'id' | 'counterpartAccountNumber' | 'mandateId' | 'creditorId'>;
type TransactionBeforeDecision = LearnedTransaction &
  Pick<Transaction, 'links' | 'remittanceInformation' | 'counterpartName'>;
type LearnedTarget = Pick<TransactionLink, 'linkId' | 'linkType'>;

const CounterpartyIdentitiesContext = createContext<CounterpartyIdentitiesContextType | null>(null);

interface CounterpartyIdentitiesContextProviderProps {
  children?: ReactNode;
}

export const CounterpartyIdentitiesContextProvider = ({ children }: CounterpartyIdentitiesContextProviderProps) => {
  const [state, dispatch] = useReducer<Reducer<CounterpartyIdentitiesState, CounterpartyIdentitiesAction>>(
    counterpartyIdentitiesReducer,
    INITIAL_COUNTERPARTY_IDENTITIES_STATE
  );
  const { clientId, isFetchingUser, rootUser } = useUser();
  const { leases, leasesLoading } = useLeases();
  const { financialTransactionsDelete, loading: permissionsLoading } = usePermissions();

  const canFetch = !isFetchingUser && !permissionsLoading && !isNil(clientId);
  const loading = state.loading || (!rootUser && Boolean(leasesLoading));

  const counterpartyIdentities = useMemo<CounterpartyIdentity[]>(() => {
    const identities = (state.identities ?? []).filter((identity) => !identity._deleted);
    if (rootUser) {
      return identities;
    }
    return filterIdentitiesVisibleOnLeases(
      identities,
      state.roles.filter((role) => !role._deleted),
      new Set(leases.map((lease) => lease.id))
    );
  }, [state.identities, state.roles, leases, rootUser]);

  const counterpartyRoles = useMemo<CounterpartyRole[]>(() => {
    const roles = state.roles.filter((role) => !role._deleted);
    if (rootUser) {
      return roles;
    }
    const leaseIds = new Set(leases.map((lease) => lease.id));
    return roles.filter((role) => role.targetType !== CounterpartyRoleTargetType.LEASE || leaseIds.has(role.targetId));
  }, [state.roles, leases, rootUser]);

  const counterpartyIdentityLookup = useMemo<CounterpartyIdentityLookup>(
    () => createCounterpartyIdentityLookup(counterpartyIdentities, counterpartyRoles),
    [counterpartyIdentities, counterpartyRoles]
  );

  useEffect(() => {
    if (canFetch) {
      dispatch({ type: 'SHOULD_FETCH' });
    }
  }, [clientId, canFetch]);

  const currentClientIdRef = useRef<string | null>(clientId);
  currentClientIdRef.current = clientId;

  useEffect(() => {
    const fetchAndSet = async (workspaceId: string) => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const [identities, roles] = await Promise.all([
          list<CounterpartyIdentity>(
            LIST_COUNTERPARTY_IDENTITIES_QUERY,
            'clientId',
            getTableClientId(workspaceId, COUNTERPARTY_IDENTITY_TABLE_NAME)
          ),
          list<CounterpartyRole>(
            LIST_COUNTERPARTY_ROLES_QUERY,
            'clientId',
            getTableClientId(workspaceId, COUNTERPARTY_ROLE_TABLE_NAME)
          ),
        ]);
        // The workspace changed while loading: its request was dropped as a duplicate, so it is replayed here
        if (currentClientIdRef.current !== workspaceId) {
          dispatch({ type: 'STALE' });
          return;
        }
        dispatch({ type: 'FETCHED', payload: { identities, roles } });
      } catch (error) {
        dispatch(
          currentClientIdRef.current === workspaceId
            ? { type: 'ERROR', payload: { error: String(error) } }
            : { type: 'STALE' }
        );
      }
    };
    if (state.shouldFetch && !state.loading && canFetch && !isNil(clientId)) {
      fetchAndSet(clientId);
    }
  }, [state.shouldFetch, state.loading, canFetch, clientId]);

  const updateIdentityStatus = async (
    identity: CounterpartyIdentity,
    status: CounterpartyIdentityStatus
  ): Promise<CounterpartyIdentity> => {
    const fetchedIdentity = await get<CounterpartyIdentity>(getCounterpartyIdentityQuery, identity.id);
    const result = await mutation<CounterpartyIdentity>(updateCounterpartyIdentityMutation, {
      input: { ...cleanInputUpdate({ id: identity.id, status }, false), _version: fetchedIdentity?._version },
    });
    dispatch({ type: 'UPDATE_IDENTITY', payload: { identity: result } });
    return result;
  };

  const suspendIdentity = (identity: CounterpartyIdentity): Promise<CounterpartyIdentity> =>
    updateIdentityStatus(identity, CounterpartyIdentityStatus.SUSPENDED);

  const reactivateIdentity = (identity: CounterpartyIdentity): Promise<CounterpartyIdentity> =>
    updateIdentityStatus(identity, CounterpartyIdentityStatus.ACTIVE);

  const deleteIdentity = async (identity: CounterpartyIdentity): Promise<CounterpartyIdentity | null> => {
    if (!financialTransactionsDelete) {
      return null;
    }
    const result = await deleteAndHideEntityWithFetchBefore<CounterpartyIdentity>(
      identity,
      getCounterpartyIdentityQuery,
      deleteCounterpartyIdentityMutation
    );
    // A single write: the backend takes the holder's roles away with the identity (counterpartyIdentityStream)
    const roleIds = getRolesDeletedWithIdentity(identity, state.identities ?? [], state.roles).map((role) => role.id);
    dispatch({ type: 'DELETE_IDENTITY', payload: { id: identity.id, roleIds } });
    return result;
  };

  // Learning is best effort on the backend: only the register itself tells whether this decision taught it
  const readBackPointIdentities = async (transaction: LearnedTransaction): Promise<CounterpartyIdentity[]> => {
    if (isNil(clientId)) {
      return [];
    }
    const identities = compact(
      await Promise.all(
        getPointIdentityIdsOfTransaction(clientId, transaction).map((id) =>
          get<CounterpartyIdentity>(getCounterpartyIdentityQuery, id)
        )
      )
    );
    dispatch({ type: 'UPSERT_RECORDS', payload: { identities, roles: [] } });
    return identities;
  };

  const readBackRoles = async (roleIds: string[]): Promise<CounterpartyRole[]> => {
    if (isEmpty(roleIds)) {
      return [];
    }
    const roles = compact(await Promise.all(roleIds.map((id) => get<CounterpartyRole>(getCounterpartyRoleQuery, id))));
    dispatch({ type: 'UPSERT_RECORDS', payload: { identities: [], roles } });
    return roles;
  };

  const isExclusionLearnedFromTransaction = async (transaction: LearnedTransaction): Promise<boolean> => {
    const identities = await readBackPointIdentities(transaction);
    const learnedIdentity = identities.find((identity) => hasActiveEvidenceOfTransaction(identity, transaction.id));
    return !isNil(learnedIdentity?.exclusionReason);
  };

  const isLearnedFromTransaction = async (
    transaction: TransactionBeforeDecision,
    target: LearnedTarget
  ): Promise<boolean> => {
    const identities = await readBackPointIdentities(transaction);
    const learnedIdentity = identities.find((identity) => hasActiveEvidenceOfTransaction(identity, transaction.id));
    const learnedRoleId = isNil(learnedIdentity) ? null : getLearnedRoleId(learnedIdentity, target);
    const matchedIdentity = isNil(clientId)
      ? undefined
      : findCounterpartyIdentity(counterpartyIdentityLookup, clientId, transaction);
    const removedTargetRoleIds = getRoleIdsOfRemovedTargets(
      compact([...identities, matchedIdentity]),
      transaction.links ?? [],
      target
    );
    const roles = await readBackRoles(uniq(compact([learnedRoleId, ...removedTargetRoleIds])));
    if (isNil(learnedIdentity)) {
      return false;
    }
    if (isNil(learnedRoleId)) {
      return true;
    }
    const learnedRole = roles.find((role) => role.id === learnedRoleId);
    return !isNil(learnedRole) && hasActiveEvidenceOfTransaction(learnedRole, transaction.id);
  };

  const refetchCounterpartyIdentities = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  const values = useMemo<CounterpartyIdentitiesContextType>(
    () => ({
      counterpartyIdentities,
      counterpartyRoles,
      counterpartyIdentityLookup,
      counterpartyIdentitiesFetched: !isNil(state.identities),
      counterpartyIdentitiesLoading: loading,
      counterpartyIdentitiesError: state.error,
      refetchCounterpartyIdentities,
      suspendIdentity,
      reactivateIdentity,
      deleteIdentity,
      isLearnedFromTransaction,
      isExclusionLearnedFromTransaction,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state,
      counterpartyIdentities,
      counterpartyRoles,
      counterpartyIdentityLookup,
      loading,
      financialTransactionsDelete,
      clientId,
    ]
  );

  return <CounterpartyIdentitiesContext.Provider value={values}>{children}</CounterpartyIdentitiesContext.Provider>;
};

export const useCounterpartyIdentities = (): CounterpartyIdentitiesContextType => {
  const context = useContext<CounterpartyIdentitiesContextType | null>(CounterpartyIdentitiesContext);
  if (isNil(context)) {
    throw new Error(
      '`useCounterpartyIdentities` hook must be used within a `CounterpartyIdentitiesContextProvider` component'
    );
  }
  return context;
};
